import { FC } from 'react';
import cx from 'classnames';

import { Card } from '../Card';
import { RoundedButton } from '../RoundedButton';
import {
  isCardWImage,
  isCardWIcon,
  isRoundedButtonLink,
  CardWIcon,
  CardWImage,
  RoundedButtonLink,
} from '../types';

import styles from './List.module.scss';

type ListProps = {
  className?: string;
  items: Array<CardWIcon | CardWImage | RoundedButtonLink>;
  columns?: number;
  onFocus?: (event: any) => void;
};

export const List: FC<ListProps> = ({ className, items, columns }) => {
  const iconItems = items.filter((child) => {
    return isCardWIcon(child);
  });
  const imageItems = items.filter((child) => {
    return isCardWImage(child);
  });

  const hasImageCards = imageItems.length > 0;
  const hasSplitLayout = columns === 2 && hasImageCards;

  // If we have a split layout (icon items in col 1, image card in col 2)
  if (hasSplitLayout) {
    return (
      <div className={cx(styles.List_splitColumns, className)}>
        <ul className={styles.List}>
          {iconItems.map((item, i) => {
            return (
              <li key={`main-menu-list-item-${i}-${item.href}`}>
                <Card {...item} tabIndex={-1} />
              </li>
            );
          })}
        </ul>
        <div className={styles.List__imageColumn}>
          {imageItems.map((item, i) => {
            return (
              <Card key={`main-menu-image-item-${i}`} {...item} tabIndex={-1} />
            );
          })}
        </div>
      </div>
    );
  }

  // Original layout for non-split cases
  const isImagesGrid = items.some((child) => {
    return isCardWImage(child);
  });

  // rounded buttons should span the full width if there are an even number of other nav items,
  // for odd number of items it should be in a single column (shares a row with another item)
  const nonRoundedCount = items.filter((child) => {
    return !isRoundedButtonLink(child);
  }).length;
  const roundedButtonSpansFull =
    columns === 2 && nonRoundedCount > 0 && nonRoundedCount % 2 === 0;

  return (
    <ul
      className={cx(
        styles.List,
        { [styles.List_large]: isImagesGrid },
        { [styles.List_twoColumns]: columns === 2 },
        className,
      )}
    >
      {items.map((item, i) => {
        if (isRoundedButtonLink(item)) {
          return (
            <li
              key={`main-menu-list-item-${i}-${item.href}`}
              className={cx({
                [styles.itemFullWidth]: roundedButtonSpansFull,
              })}
            >
              <RoundedButton
                {...item}
                tabIndex={-1}
                fullWidth={roundedButtonSpansFull}
              />
            </li>
          );
        }

        return (
          <li key={`main-menu-list-item-${i}-${item.href}`}>
            <Card {...item} tabIndex={-1} />
          </li>
        );
      })}
    </ul>
  );
};
